"use client";

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import type { RadiumShowcaseModel } from "@/lib/models-showcase";
import { modelDetailCardTopAccentClass, modelEquivalentBadgeClass } from "@/lib/models-showcase";
import { cn } from "@/lib/utils";

interface Props {
  model: RadiumShowcaseModel;
}

const formatPriceDecimal = (n: number) => `$${n.toFixed(2)}`;

/**
 * Detail card — parity with `radium-outcomes` `ModelsPage` + `ModelsPage.module.css`
 * (detailCard, cardModelName, equivalentsLabel, pricingSummary, accentHal/Clarke/Tycho).
 */
export const ModelsModelCard = ({ model }: Props) => (
  <Card
    data-model-id={model.id}
    className={cn(
      "flex h-full min-h-0 flex-col gap-0 overflow-hidden rounded-[length:var(--ds-radius-lg)] border border-[color:var(--ds-color-border-subtle)] bg-card p-0",
      "shadow-none transition-[border-color,box-shadow] duration-[length:var(--ds-motion-duration-fast)] ease-[var(--ds-motion-ease-default)]",
      "hover:border-[color:var(--ds-color-border-default)] hover:shadow-[var(--ds-shadow-md)]",
      modelDetailCardTopAccentClass(model.id),
    )}
  >
    <CardHeader className="gap-0 px-[length:var(--ds-space-5)] pb-0 pt-[length:var(--ds-space-5)]">
      <CardTitle className="m-0 text-[length:var(--ds-type-size-lg)] font-bold tracking-[length:var(--ds-type-tracking-tight)] text-foreground">
        {model.marketingName}
      </CardTitle>
      <CardDescription className="mt-[length:var(--ds-space-0-5)] text-[length:var(--ds-type-size-sm)] text-[color:var(--ds-color-text-tertiary)]">
        {model.tagline}
      </CardDescription>
    </CardHeader>
    <CardContent className="flex min-h-0 flex-1 flex-col gap-[length:var(--ds-space-4)] px-[length:var(--ds-space-5)] pb-[length:var(--ds-space-5)] pt-[length:var(--ds-space-4)] text-foreground">
      <p className="m-0 text-[length:var(--ds-type-size-sm)] leading-relaxed text-muted-foreground">
        {model.description}
      </p>

      <div className="mt-auto flex min-h-0 flex-col gap-[length:var(--ds-space-4)]">
        <div className="flex flex-col gap-[length:var(--ds-space-2)]">
          <p className="m-0 text-[length:var(--ds-type-size-xs)] font-semibold tracking-wide text-[color:var(--ds-color-text-tertiary)] uppercase">
            Equivalent to
          </p>
          <div className="flex flex-wrap gap-[length:var(--ds-space-2)]">
            {model.equivalents.map((label) => (
              <span key={label} className={modelEquivalentBadgeClass(model.id)}>
                {label}
              </span>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-x-[length:var(--ds-space-4)] gap-y-[length:var(--ds-space-2)] border-t border-[color:var(--ds-color-border-subtle)] pt-[length:var(--ds-space-3)]">
          <div className="flex min-w-0 flex-col gap-[length:var(--ds-space-0-5)]">
            <span className="text-[length:var(--ds-type-size-xs)] text-[color:var(--ds-color-text-tertiary)]">
              Input
            </span>
            <span className="block font-sans text-[length:var(--ds-type-size-base)] font-semibold text-foreground">
              {formatPriceDecimal(model.inputPerMTok)}
              <span className="text-[length:var(--ds-type-size-xs)] font-normal text-[color:var(--ds-color-text-tertiary)]">
                {" "}
                / MTok
              </span>
            </span>
          </div>
          <div className="flex min-w-0 flex-col gap-[length:var(--ds-space-0-5)]">
            <span className="text-[length:var(--ds-type-size-xs)] text-[color:var(--ds-color-text-tertiary)]">
              Output
            </span>
            <span className="block font-sans text-[length:var(--ds-type-size-base)] font-semibold text-foreground">
              {formatPriceDecimal(model.outputPerMTok)}
              <span className="text-[length:var(--ds-type-size-xs)] font-normal text-[color:var(--ds-color-text-tertiary)]">
                {" "}
                / MTok
              </span>
            </span>
          </div>
        </div>
      </div>
    </CardContent>
  </Card>
);
